import { FC, Fragment, useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@redux/store';
import { usePopupState } from 'material-ui-popup-state/hooks';

import MenuBar from '@3.0/MenuBar';
import DropDown from '@components/Blackbird/Menu/DropDown';
import IngestionSummary from './IngestionSummary';
import FullIngestion from './FullIngestion';
import { dimissibleLinkMap } from 'Routing/Layouts/TabbedLayout/constants/linkMap/dismissible.linkMap';
import { StudioRoutes } from 'Routing/RouteConstants';
import RotatingSpinner from '@components/Blackbird/RotatingSpinner/RotatingSpinner';
import { useIngestionLogCounts } from '../hooks/useIngestionLogCounts.query';
import DataSourceListHOC from './IngestionByDataSource/DataSourceListHOC';
import { IngestionByDataSourceProvider } from './context/DataSourceIngestion.context';
import IngestionSchedule from './IngestionSchedule';
import { IngestionScheduleProvider } from './context/IngestionSchedule.context';
import TableListHOC from './IngestionByDataSource/TableListHOC';
import BackButton from 'Modules/Studio/components/StudioModal/TitleBar/BackButton';
import {
  goToDataSourceListView,
  resetState,
} from './IngestionByDataSource/redux/IngestionDataSourceList.slice';
import { IDismissibleLinks } from '@3.0/Layouts/TabbedLayout/hooks/useDismissibleLinks';

const IngestionDropdown: FC<IDismissibleLinks> = ({ addDismissibleLink }) => {
  const popupState = usePopupState({ variant: 'popover', popupId: 'ingestionDropDown' });

  const { data: counts, refetch } = useIngestionLogCounts({ isMenuOpen: popupState.isOpen });

  const dispatch = useAppDispatch();

  const view = useAppSelector((state) => state.ingestionDataSourceList.view);

  useEffect(() => {
    // fetch counts immediately when dropdown is opened
    if (popupState.isOpen) {
      refetch();
    }
  }, [refetch, popupState.isOpen]);

  const handleGoBack = useCallback(() => {
    dispatch(goToDataSourceListView());
  }, [dispatch]);

  const closeDropdown = useCallback(() => {
    popupState.close();
  }, [popupState]);

  const onAddDismissibleLink = useCallback(() => {
    addDismissibleLink({
      path: StudioRoutes.base,
      name: StudioRoutes.ingestion.base,
      link: dimissibleLinkMap.ingestion,
    });
    closeDropdown();
  }, [addDismissibleLink, closeDropdown]);

  const handleOnClose = useCallback(() => {
    dispatch(resetState());
  }, [dispatch]);

  return (
    <DropDown
      popupState={popupState}
      label={
        <div
          data-testid="ingestion-dropdown-label"
          className="tw:flex tw:flex-row tw:items-center tw:justify-center tw:p-3 tw:gap-2"
        >
          <span className="tw:text-sm tw:font-light">Ingestion</span>
          {counts && counts.ingestionRunningCount > 0 && (
            <RotatingSpinner className="tw:ml-[4px]" style={{ fontSize: 16 }} />
          )}
        </div>
      }
      containerBorders={{
        left: true,
        right: false,
      }}
      permissionRef={StudioRoutes.ingestionLog.permission}
      popOverProps={{
        className: 'ingestion-dropdown-popover',
        onClose: handleOnClose,
      }}
    >
      <IngestionScheduleProvider>
        <div
          className="tw:w-[489px] tw:rounded-t-none tw:flex tw:flex-col tw:h-[calc(100vh-40px)] overflow-hidden"
          data-testid="ingestion-dropdown"
        >
          <MenuBar
            label={
              view === 'tables' ? (
                <BackButton
                  showBackBtn
                  onClick={handleGoBack}
                  className="tw:!h-[40px] tw:!py-0"
                  data-theme="dark"
                />
              ) : (
                ''
              )
            }
            actionButtons={[<IngestionSummary isMenuOpen={popupState.isOpen} />]}
            actionButtonsClassName="tw:bg-transparent"
            paddingLeft={false}
            className="tw:!border-t-0 tw:shrink-0"
          />
          <IngestionByDataSourceProvider>
            {view === 'tables' ? (
              <TableListHOC onAddDismissibleLink={onAddDismissibleLink} />
            ) : (
              <Fragment>
                <FullIngestion onAddDismissibleLink={onAddDismissibleLink} />
                <IngestionSchedule />
                <DataSourceListHOC />
              </Fragment>
            )}
          </IngestionByDataSourceProvider>
        </div>
      </IngestionScheduleProvider>
    </DropDown>
  );
};

export default IngestionDropdown;
